import React, { useMemo } from 'react';
import { Building2, Edit, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { accessProfilesAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { organizationOption } from '../../lib/selectOptions';
import { messageErreur } from '../../lib/erreurs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { LoadingState } from '../ui/loading-state';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { SearchableSelect } from '../ui/searchable-select';
import { pluriel } from './ProfileDialog';

/**
 * Bouton « Profils (n) » de la page Utilisateurs et sa liste compacte: chaque
 * profil d'accès (même sans personne), son nombre de comptes — un clic filtre
 * la liste des comptes sur ce profil — et ses actions. Les données et la
 * fenêtre de création/modification sont tenues par la page.
 */
export const ProfilesPopover = ({
  open,
  onOpenChange,
  count,
  profiles,
  loading,
  organizations,
  organizationId,
  onOrganizationChange,
  onEdit,
  onDeleted,
  onSeeAccounts,
}) => {
  const { isSuperAdmin } = useAuth();
  const organizationOptions = useMemo(() => organizations.map(organizationOption), [organizations]);
  const peutCharger = !isSuperAdmin || Boolean(organizationId);

  const supprimer = async (profil) => {
    if (!window.confirm(`Supprimer le profil « ${profil.name} » ?`)) return;
    try {
      await accessProfilesAPI.remove(profil.id, isSuperAdmin ? profil.organization_id || organizationId : undefined);
      toast.success('Profil supprimé');
      onDeleted?.(profil);
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de supprimer ce profil'));
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="profils-bouton">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          {count === null ? 'Profils' : `Profils (${count})`}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Profils d’accès"
        data-testid="profils-liste"
        className="max-h-[70vh] w-[26rem] max-w-[calc(100vw-2rem)] space-y-2 overflow-y-auto p-2"
      >
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden="true" />
            <SearchableSelect
              value={organizationId}
              onValueChange={onOrganizationChange}
              aria-label="Société des profils"
              placeholder="Choisir la société"
              searchPlaceholder="Rechercher une société…"
              emptyMessage="Aucune société trouvée"
              options={organizationOptions}
              className="h-9 w-full"
            />
          </div>
        )}

        {loading ? (
          <LoadingState shape="table" />
        ) : !peutCharger ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Choisissez une société</p>
        ) : profiles.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Aucun profil pour le moment</p>
        ) : (
          <ul className="divide-y divide-border">
            {profiles.map((profil) => (
              <li
                key={profil.id}
                data-testid={`profile-${profil.id}`}
                className="flex items-center justify-between gap-2 px-2 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{profil.name}</p>
                  <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
                    <span>{pluriel((profil.modules || []).length, 'module')}</span>
                    <span aria-hidden="true">·</span>
                    <button
                      type="button"
                      data-testid={`profile-comptes-${profil.id}`}
                      title="Voir les comptes de ce profil"
                      onClick={() => onSeeAccounts(profil)}
                      className="font-medium text-info hover:underline"
                    >
                      {pluriel(profil.users_count || 0, 'compte')}
                    </button>
                    {profil.can_see_money ? <Badge variant="info">Montants visibles</Badge> : null}
                    {profil.login_method === 'code' ? <Badge variant="muted">Connexion par code</Badge> : null}
                  </p>
                </div>
                <div className="flex shrink-0 gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Modifier"
                    aria-label={`Modifier ${profil.name}`}
                    onClick={() => onEdit(profil)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {!profil.is_system && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Supprimer"
                      aria-label={`Supprimer ${profil.name}`}
                      onClick={() => supprimer(profil)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ProfilesPopover;
